import * as React from "react"
import { authApi, type Me, type Membership } from "@/api/auth"

type AuthContextValue = {
  state: "loading" | "authenticated" | "anonymous"
  user: Extract<Me, { authenticated: true }>["user"] | null
  memberships: Membership[]
  activeOrg: Membership | null
  refresh: () => Promise<void>
  switchOrg: (organizationId: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

const ACTIVE_ORG_COOKIE = "active_org_id"

function readActiveOrgCookie(): string | null {
  if (typeof document === "undefined") return null
  return (
    document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${ACTIVE_ORG_COOKIE}=`))
      ?.split("=")[1] ?? null
  )
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<AuthContextValue["state"]>("loading")
  const [user, setUser] = React.useState<AuthContextValue["user"]>(null)
  const [memberships, setMemberships] = React.useState<Membership[]>([])

  const refresh = React.useCallback(async () => {
    const me = await authApi.me()
    if (!me.authenticated) {
      setState("anonymous")
      setUser(null)
      setMemberships([])
      return
    }
    setState("authenticated")
    setUser(me.user)
    setMemberships(me.memberships)
  }, [])

  React.useEffect(() => {
    refresh().catch(() => setState("anonymous"))
  }, [refresh])

  const activeOrg = React.useMemo<Membership | null>(() => {
    if (memberships.length === 0) return null
    const id = readActiveOrgCookie()
    return memberships.find((m) => m.organization_id === id) ?? memberships[0]
  }, [memberships])

  const switchOrg = React.useCallback(
    async (organizationId: string) => {
      await authApi.switchOrg({ organization_id: organizationId })
      await refresh()
    },
    [refresh],
  )

  const signOut = React.useCallback(async () => {
    await authApi.logout()
    setState("anonymous")
    setUser(null)
    setMemberships([])
  }, [])

  const value = React.useMemo<AuthContextValue>(
    () => ({ state, user, memberships, activeOrg, refresh, switchOrg, signOut }),
    [state, user, memberships, activeOrg, refresh, switchOrg, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>")
  return ctx
}
